import { useCallback, useEffect, useRef, useState } from "react";
import { useFollowViewMode } from "../utils/useFollowViewMode";
import { pollProgress } from "../utils/webProgressPoll";
import { getTigerResource } from "../utils/tigerResource";

/**
 * The live diagnostics messages window — a small panel at the bottom of the live
 * UI showing the most recent DV_ALARM lines (C++ `MessagesWindow`). Fed by the
 * backend `dv-alarm` event. Visibility and clear are driven from the Diagnostics
 * menu (via the `visible` / `clearSignal` props the parent controls).
 *
 * Each line carries its turbine (`config`) and source; C++ keeps a separate alarm
 * window per turbine AND source and swaps which one is shown on a turbine change
 * (`Ui_change_to_turbine`) or a live/replay switch (`Ui_change_dv_run_state`), so
 * we tag every line and show only those for the currently-selected turbine and mode.
 *
 * The panel is resizable by dragging its top edge; the height is remembered
 * across sessions and decides how many lines are shown.
 */
interface DiagMsg {
  id: number;
  severity: number;
  text: string;
  source: number;
  config: string;
}

/** `DATA_SOURCES::FILE_REPLAY` — a DV_ALARM with this source belongs to replay. */
const FILE_REPLAY_SOURCE = 2;

/** Number of lines shown at the default height (C++ keeps up to 100). */
const VISIBLE_LINES = 3;
/** How many to retain so older lines reappear as newer ones are cleared. */
const RETAIN = 50;

/** Rendered height of one message line — must match `--diag-msg-line-height`. */
const LINE_HEIGHT = 17;
/** Vertical padding of `.diag-messages-panel` (2px top + 2px bottom). */
const PANEL_PADDING = 4;
const DEFAULT_HEIGHT = VISIBLE_LINES * LINE_HEIGHT + PANEL_PADDING;
const MIN_HEIGHT = LINE_HEIGHT + PANEL_PADDING;
/** Never taller than the retained backlog, nor than most of the window. */
function maxHeight(): number {
  const viewport = typeof window === "undefined" ? 0 : window.innerHeight * 0.6;
  return Math.max(MIN_HEIGHT, Math.min(RETAIN * LINE_HEIGHT + PANEL_PADDING, viewport));
}
function clampHeight(h: number): number {
  return Math.min(maxHeight(), Math.max(MIN_HEIGHT, Math.round(h)));
}

/** Remembered panel height (per browser / desktop profile). */
const HEIGHT_KEY = "tiger.diagMessagesHeight";

function loadHeight(): number {
  try {
    const n = Number(window.localStorage.getItem(HEIGHT_KEY));
    if (Number.isFinite(n) && n > 0) return clampHeight(n);
  } catch {
    /* storage disabled (private window) — fall back to the default */
  }
  return DEFAULT_HEIGHT;
}

function saveHeight(h: number): void {
  try {
    window.localStorage.setItem(HEIGHT_KEY, String(h));
  } catch {
    /* storage disabled — the height simply isn't remembered */
  }
}

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** Severity → line colour (higher severity = more urgent). */
function severityColour(severity: number): string {
  if (severity >= 3) return "#e03131"; // alarm — red
  if (severity === 2) return "#e8590c"; // warning — orange
  return "#343a40"; // normal
}

export function DiagnosticsMessagesPanel({
  visible,
  clearSignal,
  config,
}: {
  visible: boolean;
  clearSignal: number;
  /** The currently-selected turbine (config_name). Only that turbine's lines show
   *  — mirrors the C++ per-turbine alarm window swapped in `Ui_change_to_turbine`.
   *  Empty (nothing selected) shows all turbines' lines as a safe fallback. */
  config: string;
}) {
  const [messages, setMessages] = useState<DiagMsg[]>([]);
  const [height, setHeight] = useState(loadHeight);
  // Current run mode — show only the matching source's lines, swapping on switch.
  const [isLive, setIsLive] = useState(true);
  useFollowViewMode(setIsLive);
  const nextId = useRef(0);

  // Subscribe to the backend events that drive the panel:
  //  - `dv-alarm`: append a diagnostics line.
  //  - `replay-started`: a replay (re)start clears the replay alarm-message stack
  //    (C++ Ti_start_replay -> Ui_clear_alarm_message_window(.., FILE_REPLAY)) so a
  //    fresh run doesn't carry the previous run's messages. Only the replay-source
  //    lines are dropped; live lines are kept for when the user switches back.
  // Both are registered in one effect / one import so neither subscription is lost.
  useEffect(() => {
    if (!isTauri()) return;
    let cancelled = false;
    const offs: Array<() => void> = [];
    void import("@tauri-apps/api/event").then(async ({ listen }) => {
      const offAlarm = await listen<{ config: string; source: number; severity: number; message: string }>(
        "dv-alarm",
        (e) => {
          setMessages((prev) => {
            const next = [...prev, { id: nextId.current++, severity: e.payload.severity, text: e.payload.message, source: e.payload.source, config: e.payload.config }];
            return next.slice(-RETAIN);
          });
        },
      );
      const offStarted = await listen("replay-started", () => {
        setMessages((prev) => prev.filter((m) => m.source !== FILE_REPLAY_SOURCE));
      });
      if (cancelled) { offAlarm(); offStarted(); }
      else offs.push(offAlarm, offStarted);
    });
    return () => {
      cancelled = true;
      offs.forEach((o) => o());
    };
  }, []);

  // Web has no event channel, so poll the backend's rolling DV_ALARM buffer and
  // render it (the buffer is global — DV_ALARMs are alarm-server broadcasts, like
  // the shared live data). The mode filter / height-based slice below still apply.
  useEffect(() => {
    if (isTauri()) return;
    return pollProgress<{ config: string; source: number; severity: number; message: string }[]>(
      "/api/get_dv_alarms",
      (lines) =>
        setMessages((lines ?? []).map((l, i) => ({ id: i, severity: l.severity, text: l.message, source: l.source, config: l.config }))),
    );
  }, []);

  // Clear when the Diagnostics-menu "Clear" action bumps the signal. On web also
  // clear the backend buffer (else the next poll refills it); skip the initial
  // mount run so opening the panel doesn't wipe existing messages.
  const clearedOnce = useRef(false);
  useEffect(() => {
    setMessages([]);
    if (!isTauri() && clearedOnce.current) void fetch("/api/clear_dv_alarms");
    clearedOnce.current = true;
  }, [clearSignal]);

  // Drag the top edge to resize (same idiom as SplitPane: refs + document
  // listeners so the drag survives the pointer leaving the 4px handle).
  const dragStartY = useRef(0);
  const dragStartHeight = useRef(0);
  const dragging = useRef(false);
  const onHandleMouseDown = useCallback((e: React.MouseEvent) => {
    dragging.current = true;
    dragStartY.current = e.clientY;
    dragStartHeight.current = height;
    e.preventDefault();
  }, [height]);

  useEffect(() => {
    // Dragging up (a smaller clientY) makes the panel taller.
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      setHeight(clampHeight(dragStartHeight.current - (e.clientY - dragStartY.current)));
    };
    const onMouseUp = () => {
      if (!dragging.current) return;
      dragging.current = false;
      setHeight((h) => { saveHeight(h); return h; });
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  if (!visible) return null;

  // Only the currently-selected turbine's lines, in the current mode (replay =
  // FILE_REPLAY source; live = the rest). C++ swaps the per-turbine window on a
  // turbine change; here the config filter re-runs and the panel swaps in place.
  // An empty config (nothing selected yet) shows all as a safe fallback.
  const forTurbineAndMode = messages.filter(
    (m) => (m.source === FILE_REPLAY_SOURCE) === !isLive && (!config || m.config === config),
  );
  // As many of the newest lines as the current height has room for.
  const shown = forTurbineAndMode.slice(-Math.max(1, Math.round((height - PANEL_PADDING) / LINE_HEIGHT)));

  return (
    <>
      <div
        className="diag-messages-resizer"
        role="separator"
        aria-orientation="horizontal"
        aria-label={getTigerResource("TIGER_DIAG_RESIZE_MESSAGES")}
        title={getTigerResource("TIGER_DIAG_RESIZE_MESSAGES")}
        onMouseDown={onHandleMouseDown}
        onDoubleClick={() => { setHeight(DEFAULT_HEIGHT); saveHeight(DEFAULT_HEIGHT); }}
      />
      <div
        className="diag-messages-panel"
        style={{ height }}
        role="log"
        aria-label={getTigerResource("TIGER_DIAG_MESSAGES_PANEL")}
      >
        {shown.length === 0 ? (
          <div className="diag-msg-line diag-msg-empty">{getTigerResource("TIGER_DIAG_NO_MESSAGES")}</div>
        ) : (
          shown.map((m) => (
            <div key={m.id} className="diag-msg-line" style={{ color: severityColour(m.severity) }}>
              {m.text}
            </div>
          ))
        )}
      </div>
    </>
  );
}
